/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useState } from "react";
import { Col, Divider, Row } from "antd";
import { FlexCol } from "../../../components/Container/Container";
import Text from "../../../components/Text/Text";
import { newsTypes } from "../../../definitions/news";
import color from "../../../resource/color";
import { NewsAppPreview } from "./NewsAppPreview";
import { NewsShop, NewsShopEmpty } from "./NewsStepTwoShop";
import TablePagination from "../../../components/Table/TablePagination";

const PAGE_SIZE = 7;

const appLabel: Record<string, string> = {
  isShowOnShopApp: "Shop App",
  isShowOnSaleApp: "Sales App",
};

const InfoBox = ({ label, value }: { label: string; value: string }) => (
  <div
    style={{
      background: color.background1,
      border: `1px solid ${color.background1}`,
      borderRadius: 8,
      padding: "12px 16px",
      height: "100%",
    }}
  >
    <Text fontSize={12} fontFamily='Sarabun' color='grayishBlue'>
      {label}
    </Text>
    <br />
    <Text fontSize={14} color='Text2' fontFamily='Sarabun'>
      {value}
    </Text>
  </div>
);

interface Props {
  values: any;
  imgUrl?: string;
  topic: string;
  content: string;
  status?: string;
  shops: NewsShop[];
}

export const NewsStepThree: React.FC<Props> = ({
  values,
  imgUrl,
  topic,
  content,
  status,
  shops,
}) => {
  const { app, type, startDate, startTime } = values || {};
  const newsTopic = values?.topic || topic;

  const [page, setPage] = useState<number>(1);
  const [sorter, setSorter] = useState<{ field?: string; order?: "ascend" | "descend" }>({});

  const total = shops?.length || 0;

  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [total, page]);

  const sortedShops = useMemo(() => {
    const list = [...(shops || [])];
    const { field, order } = sorter;
    if (!field || !order) return list;
    return list.sort((a: any, b: any) => {
      const result = `${a?.[field] ?? ""}`.localeCompare(`${b?.[field] ?? ""}`);
      return order === "ascend" ? result : -result;
    });
  }, [shops, sorter]);

  const pagedShops = useMemo(
    () =>
      sortedShops.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map((shop, index) => ({
        ...shop,
        key: `${shop.customerCompanyId ?? ""}-${index}`,
      })),
    [sortedShops, page],
  );

  const publishTimeText = startDate
    ? `${startDate.format("D/M/")}${startDate.year() + 543}, ${(startTime || startDate).format(
        "HH:mm",
      )} น.`
    : "-";

  const statusText = () => {
    switch (status) {
      case "WAITING":
        return `ตั้งเวลาเผยแพร่ : ${publishTimeText}`;
      case "DRAFT":
        return "แบบร่าง";
      case "INACTIVE":
        return "ปิดใช้งาน";
      default:
        return "เผยแพร่ทันที";
    }
  };

  const onTableChange = (_pagination: any, _filters: any, tableSorter: any) => {
    const current = Array.isArray(tableSorter) ? tableSorter[0] : tableSorter;
    setSorter({ field: `${current?.field ?? ""}` || undefined, order: current?.order });
    setPage(1);
  };

  const columns = [
    {
      title: "ร้านค้า",
      dataIndex: "customerName",
      key: "customerName",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any, row: NewsShop) => (
        <FlexCol>
          <Text level={5} color='Text2'>
            {value || "-"}
          </Text>
          {row.customerNo && (
            <Text level={6} color='Text3'>
              {row.customerNo}
            </Text>
          )}
        </FlexCol>
      ),
    },
    {
      title: "เขต",
      dataIndex: "zone",
      key: "zone",
      width: "25%",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any) => <Text level={5}>{value || "-"}</Text>,
    },
    {
      title: "กลุ่มลูกค้า",
      dataIndex: "customerGroupName",
      key: "customerGroupName",
      width: "25%",
      sorter: true,
      showSorterTooltip: false,
      render: (value: any) => <Text level={5}>{value || "-"}</Text>,
    },
  ];

  return (
    <>
      <Row gutter={24}>
        <Col style={{ width: "calc(100% - 320px)" }}>
          <Text level={3} fontWeight={700}>
            ตรวจสอบข้อมูล
          </Text>
          <Row gutter={[16, 16]} style={{ paddingTop: 16 }}>
            <Col span={12}>
              <InfoBox label='หัวข้อข่าว' value={newsTopic || "-"} />
            </Col>
            <Col span={12}>
              <InfoBox
                label='หมวดหมู่'
                value={newsTypes[type as keyof typeof newsTypes]?.name || "-"}
              />
            </Col>
            <Col span={12}>
              <InfoBox
                label='แอปพลิเคชันที่เผยแพร่'
                value={
                  app?.length > 0
                    ? app
                        .map((a: string) => appLabel[a])
                        .filter(Boolean)
                        .join(", ")
                    : "-"
                }
              />
            </Col>
            <Col span={12}>
              <InfoBox label='สถานะ' value={statusText()} />
            </Col>
          </Row>
          <Divider />
          <Row align='middle' style={{ paddingBottom: 16 }}>
            <Text level={5} fontWeight={700}>
              {`ร้านค้าที่เห็นข่าว ${total} ร้าน`}
            </Text>
          </Row>
          <TablePagination
            columns={columns as any}
            data={pagedShops}
            keyId='key'
            locale={{ emptyText: <NewsShopEmpty text='ไม่มีร้านค้า' /> }}
            onChange={onTableChange}
            pagination={{
              current: page,
              total,
              pageSize: PAGE_SIZE,
            }}
            onPageChange={setPage}
          />
        </Col>
        <Col style={{ width: 320 }}>
          <NewsAppPreview imgUrl={imgUrl} topic={newsTopic} content={content} titleBar />
        </Col>
      </Row>
    </>
  );
};

export default NewsStepThree;
